import { ComponentType, ReactNode } from "react";

/**
 * Portable Text envelope — the general content contract for structured,
 * renderer-independent content (in-app messages, help, changelogs).
 *
 * The design system owns this envelope and the universal default set below.
 * It never hardcodes a product element: concrete elements (callout,
 * button-reference, doc-link, …) are defined in the application catalog and
 * injected into the Portable Text engine through {@link PortableTextComponents}.
 * See README.md next to this file for the full contract.
 */

/* ---- Spans and marks ------------------------------------------------- */

/**
 * A mark with data. Parameterless marks travel as plain strings ("strong");
 * marks that need data travel as objects discriminated by `type`.
 */
export interface PortableTextMarkObject {
  type: string;
  [data: string]: unknown;
}

/** The one default mark that carries data. */
export interface PortableTextLinkMark extends PortableTextMarkObject {
  type: "link";
  href: string;
}

export type PortableTextMark = string | PortableTextMarkObject;

/** The leaf of every text run: a piece of text plus the marks wrapping it. */
export interface PortableTextSpan {
  text: string;
  marks?: PortableTextMark[];
}

/** Marks every client understands without injection. */
export const PORTABLE_TEXT_DEFAULT_MARKS = [
  "strong",
  "em",
  "code",
  "link",
] as const;

export type PortableTextDefaultMark =
  (typeof PORTABLE_TEXT_DEFAULT_MARKS)[number];

/* ---- Blocks ----------------------------------------------------------- */

export interface PortableTextParagraphBlock {
  type: "paragraph";
  children: PortableTextSpan[];
}

/** 1 maps to Title1, 2 to Title2, 3 to Header2 typography. */
export type PortableTextHeadingLevel = 1 | 2 | 3;

export interface PortableTextHeadingBlock {
  type: "heading";
  level: PortableTextHeadingLevel;
  /**
   * Optional anchor id, rendered verbatim as the heading element's `id` so the
   * heading is a scroll target (e.g. subarticle deep-links). Opaque to the
   * renderer — any slug convention lives with whoever produced the block.
   */
  id?: string;
  children: PortableTextSpan[];
}

export type PortableTextListStyle = "bullet" | "number";

/**
 * A list item's children are its own spans first, then any nested `list`
 * block(s) — the shape that carries multi-level lists. A spans-only item (no
 * nested list) is the common, flat case and stays valid unchanged.
 */
export interface PortableTextListItemBlock {
  type: "list-item";
  children: (PortableTextSpan | PortableTextListBlock)[];
}

export interface PortableTextListBlock {
  type: "list";
  style?: PortableTextListStyle;
  children: PortableTextListItemBlock[];
}

/** Screenshots render framed (border, no shadow); illustrations render flat. */
export type PortableTextMediaImageVariant = "screenshot" | "illustration";

/**
 * Video variants name the hosting service; `src` is the canonical video URL.
 * Their rendering carries service semantics (URL parsing, embed derivation),
 * so it belongs to the application catalog — injected as a `media` block
 * override — never to the default set. The default renderer degrades them.
 */
export type PortableTextMediaVideoVariant = "vimeo" | "youtube";

export type PortableTextMediaVariant =
  | PortableTextMediaImageVariant
  | PortableTextMediaVideoVariant;

// A Record so the compiler checks the list against the union in both
// directions: a missing and a stray key each fail to build.
const MEDIA_IMAGE_VARIANTS: Record<PortableTextMediaImageVariant, true> = {
  screenshot: true,
  illustration: true,
};

export const isPortableTextMediaImageVariant = (
  value: unknown
): value is PortableTextMediaImageVariant =>
  typeof value === "string" && value in MEDIA_IMAGE_VARIANTS;

export interface PortableTextMediaBlock {
  type: "media";
  src: string;
  alt?: string;
  variant?: PortableTextMediaVariant;
  caption?: string;
}

export interface PortableTextTitledTextBlock {
  type: "titled-text";
  /**
   * Same semantic scale as `heading` — 1, 2 or 3 (the default) — so the wire
   * format never encodes a typography name. How a level looks is the
   * renderer's business.
   */
  level?: PortableTextHeadingLevel;
  title: string;
  content?: PortableTextNode[];
}

export interface PortableTextCodeBlock {
  type: "code-block";
  /**
   * A language hint (e.g. "php", "json"). The default renderer does no
   * highlighting, so any value — known, unknown or absent — renders as plain
   * preformatted text; it travels as data for surfaces that highlight.
   */
  language?: string;
  code: string;
}

/** A table cell is a span array, so inline marks work inside cells. */
export type PortableTextTableCell = PortableTextSpan[];
export type PortableTextTableRow = PortableTextTableCell[];

export interface PortableTextTableBlock {
  type: "table";
  header: PortableTextTableRow;
  rows: PortableTextTableRow[];
}

/** A themed horizontal rule between sections. Carries no data. */
export interface PortableTextDividerBlock {
  type: "divider";
}

export type PortableTextBlock =
  | PortableTextParagraphBlock
  | PortableTextHeadingBlock
  | PortableTextListBlock
  | PortableTextMediaBlock
  | PortableTextTitledTextBlock
  | PortableTextCodeBlock
  | PortableTextTableBlock
  | PortableTextDividerBlock;

/**
 * Derived from the union's discriminators, so adding a block to the union
 * without a renderer entry is a compile error, never silent drift.
 */
export type PortableTextDefaultBlockType = (
  | PortableTextBlock
  | PortableTextListItemBlock
)["type"];

// A Record so the compiler checks the list against the union in both
// directions: a missing and a stray key each fail to build.
const DEFAULT_BLOCK_TYPES: Record<PortableTextDefaultBlockType, true> = {
  paragraph: true,
  heading: true,
  list: true,
  "list-item": true,
  media: true,
  "titled-text": true,
  "code-block": true,
  table: true,
  divider: true,
};

/** Blocks every client understands without injection. */
export const PORTABLE_TEXT_DEFAULT_BLOCKS = Object.keys(
  DEFAULT_BLOCK_TYPES
) as readonly PortableTextDefaultBlockType[];

/* ---- Objects ----------------------------------------------------------- */

/**
 * An embedded component the envelope does not interpret: `_type` names an
 * application-catalog element, everything else is that element's data. An
 * object whose `_type` has no injected renderer renders nothing and is logged.
 */
export interface PortableTextObjectNode {
  type: "object";
  _type: string;
  [data: string]: unknown;
}

export type PortableTextNode = PortableTextBlock | PortableTextObjectNode;

/** The root of a message body: what an API returns under `content`. */
export type PortableTextContent = PortableTextNode[];

/* ---- Injection contract ------------------------------------------------ */

export interface PortableTextMarkRendererProps<
  TMark extends PortableTextMarkObject = PortableTextMarkObject
> {
  /** Present for object marks; absent when the mark is a plain string. */
  mark?: TMark;
  children: ReactNode;
}

export type PortableTextMarkRenderer<
  TMark extends PortableTextMarkObject = PortableTextMarkObject
> = ComponentType<PortableTextMarkRendererProps<TMark>>;

export interface PortableTextObjectRendererProps<
  TNode extends PortableTextObjectNode = PortableTextObjectNode
> {
  node: TNode;
  /** The object's nested `content`, already rendered by the engine. */
  children?: ReactNode;
}

export type PortableTextObjectRenderer<
  TNode extends PortableTextObjectNode = PortableTextObjectNode
> = ComponentType<PortableTextObjectRendererProps<TNode>>;

export interface PortableTextBlockRendererProps<
  TBlock extends PortableTextBlock = PortableTextBlock
> {
  block: TBlock;
  /** The block's rendered children/content, when it has any. */
  children?: ReactNode;
  /**
   * Engine-bound span renderer for blocks that carry spans outside `children`
   * (table cells). Resolves marks with the same precedence — injected first,
   * then defaults — as flowing text. Absent when a renderer is used without
   * the engine.
   */
  renderSpans?: (spans: PortableTextSpan[]) => ReactNode;
}

export type PortableTextBlockRenderer<
  TBlock extends PortableTextBlock = PortableTextBlock
> = ComponentType<PortableTextBlockRendererProps<TBlock>>;

export type PortableTextUnknownKind = "block" | "mark" | "object";

/**
 * Called once per unknown type instead of rendering it, so content written
 * for newer clients degrades silently on older ones.
 */
export type PortableTextUnknownTypeHandler = (
  kind: PortableTextUnknownKind,
  type: string
) => void;

/**
 * What an application injects into the Portable Text engine: renderers for
 * its catalog marks and objects, optional overrides for the default blocks,
 * and the unknown-type hook.
 */
export interface PortableTextComponents {
  marks?: Record<string, PortableTextMarkRenderer>;
  objects?: Record<string, PortableTextObjectRenderer>;
  blocks?: Partial<
    Record<PortableTextDefaultBlockType, PortableTextBlockRenderer>
  >;
  onUnknownType?: PortableTextUnknownTypeHandler;
}

/* ---- Runtime guards ----------------------------------------------------- */

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

export const isPortableTextSpan = (value: unknown): value is PortableTextSpan =>
  isRecord(value) && typeof value.text === "string";

export const isPortableTextTableRow = (
  value: unknown
): value is PortableTextTableRow =>
  Array.isArray(value) &&
  value.every((cell) => Array.isArray(cell) && cell.every(isPortableTextSpan));

export const isPortableTextObjectNode = (
  value: unknown
): value is PortableTextObjectNode =>
  isRecord(value) && value.type === "object" && typeof value._type === "string";

/**
 * A block is any non-object node with a string `type`. Unknown block types
 * stay valid on purpose — they render nothing on clients that predate them.
 * Known types are held to their shape, so a serializer bug (a paragraph
 * without `children`, a list of non-nodes) fails here instead of at render.
 */
export const isPortableTextBlock = (
  value: unknown
): value is PortableTextBlock => {
  if (
    !isRecord(value) ||
    typeof value.type !== "string" ||
    value.type === "object"
  ) {
    return false;
  }

  switch (value.type) {
    case "paragraph":
      return (
        Array.isArray(value.children) &&
        value.children.every(isPortableTextSpan)
      );
    case "heading":
      return (
        (value.id === undefined || typeof value.id === "string") &&
        Array.isArray(value.children) &&
        value.children.every(isPortableTextSpan)
      );
    case "list-item":
      // The item's spans, plus optional nested `list` block(s). Widened only
      // for nested lists: any other block child (a stray paragraph, an object)
      // still fails, so a serializer bug is caught here, not at render.
      return (
        Array.isArray(value.children) &&
        value.children.every(
          (child) =>
            isPortableTextSpan(child) ||
            (isPortableTextBlock(child) && child.type === "list")
        )
      );
    case "list":
      // A list holds `list-item`s only. A sublist emitted as a sibling of the
      // items (the classic half-implemented-nesting bug) renders invalid HTML
      // — <ul> with a direct <ul> child — so it fails here, not at render.
      // `list-item` is validated but sits outside the PortableTextBlock union,
      // so the discriminator is checked before isPortableTextBlock narrows it.
      return (
        Array.isArray(value.children) &&
        value.children.every(
          (child) =>
            isRecord(child) &&
            child.type === "list-item" &&
            isPortableTextBlock(child)
        )
      );
    case "media":
      return typeof value.src === "string";
    case "titled-text":
      return (
        typeof value.title === "string" &&
        (value.content === undefined || isPortableTextContent(value.content))
      );
    case "code-block":
      return (
        typeof value.code === "string" &&
        (value.language === undefined || typeof value.language === "string")
      );
    case "table":
      return (
        isPortableTextTableRow(value.header) &&
        Array.isArray(value.rows) &&
        value.rows.every(isPortableTextTableRow)
      );
    case "divider":
      return true;
    default:
      return true;
  }
};

export const isPortableTextContent = (
  value: unknown
): value is PortableTextContent =>
  Array.isArray(value) &&
  value.every(
    (node) => isPortableTextBlock(node) || isPortableTextObjectNode(node)
  );
